import { supabase } from '@/lib/supabase'
import type { User } from '@/models/supabase/User'
import { userProfileToUser } from '@/models/supabase/User'
import { detectLocale } from '@/i18n'

/**
 * Service d'authentification anonyme pour Supabase
 * Gère la connexion anonyme et la conversion vers un compte permanent
 */
export const authAnonymousService = {
    /**
     * Connexion anonyme
     */
    async signInAnonymously(): Promise<User> {
        // console.log('🔐 [Anonymous Auth] Starting anonymous sign in...')

        try {
            // 1. Reuse existing anonymous session or create new one
            let authUserId: string
            const { data: { user: currentUser } } = await supabase.auth.getUser()
            if (currentUser && currentUser.is_anonymous) {
                authUserId = currentUser.id
            } else {
                const { data: authData, error: authError } = await supabase.auth.signInAnonymously()
                if (authError) {
                    console.error('❌ [Anonymous Auth] Auth error:', authError)
                    throw authError
                }
                if (!authData.user) {
                    throw new Error('Anonymous user creation failed')
                }
                authUserId = authData.user.id
            }

            // 2. Créer le profil utilisateur anonyme
            const { data: profile, error: profileError } = await supabase
                .from('user_profiles')
                .upsert({
                    id: authUserId,
                    name: 'Utilisateur Anonyme',
                    email: null, // Pas d'email pour les utilisateurs anonymes
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    language: detectLocale(),
                    daily_reminder: false,
                    point_assignments: false,
                    reward_unlocked: false,
                    is_anonymous: true // Marquer comme anonyme
                }, {
                    onConflict: 'id'
                })
                .select()
                .single()

            if (profileError) {
                console.error('❌ [Anonymous Auth] Profile creation error:', profileError)
                // Ne pas échouer si le profil existe déjà
                if (profileError.code !== '23505') { // Code pour "duplicate key"
                    throw profileError
                }
            }

            // console.log('✅ [Anonymous Auth] Anonymous profile created/updated')

            // 3. Convertir le profil en User
            const user = userProfileToUser(profile || {
                id: authUserId,
                name: 'Utilisateur Anonyme',
                email: '',
                avatar_url: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                language: detectLocale(),
                daily_reminder: false,
                point_assignments: false,
                reward_unlocked: false,
                date_format: 'DD/MM/YYYY',
                is_anonymous: true,
                time_format: '24h',
                week_start_day: 'monday',
                email_type: 'fictive',
                user_type: 'parent',
                onboarding_opt_out: false
            })

            // console.log('✅ [Anonymous Auth] Anonymous authentication successful')
            return user

        } catch (error) {
            console.error('❌ [Anonymous Auth] Anonymous sign in failed:', error)
            throw error
        }
    },

    /**
     * Vérifier si l'utilisateur actuel est anonyme
     */
    async isCurrentUserAnonymous(): Promise<boolean> {
        try {
            const { data: { user } } = await supabase.auth.getUser()

            if (!user) return false

            // Vérifier dans le JWT si l'utilisateur est anonyme
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.user) {
                const isAnonymous = (session.user as any).is_anonymous === true
                return isAnonymous
            }

            return false
        } catch (error) {
            console.error('❌ [Anonymous Auth] Error checking anonymous status:', error)
            return false
        }
    },

    /**
     * Convertir un compte anonyme en compte permanent
     */
    async upgradeToPermanentAccount(email: string, password: string, name: string): Promise<User> {
        // console.log('🔄 [Anonymous Auth] Upgrading anonymous account to permanent...')

        try {
            const { data: { user: currentUser } } = await supabase.auth.getUser()

            if (!currentUser) {
                throw new Error('No current user found')
            }

            // 1. Mettre à jour l'email et le mot de passe
            const { error: updateError } = await supabase.auth.updateUser({
                email: email.trim(),
                password: password,
                data: {
                    name: name.trim()
                }
            })

            if (updateError) {
                console.error('❌ [Anonymous Auth] Update user error:', updateError)
                throw updateError
            }

            // 2. Mettre à jour le profil utilisateur
            const { data: updatedProfile, error: profileError } = await supabase
                .from('user_profiles')
                .update({
                    name: name.trim(),
                    email: email.trim(),
                    updated_at: new Date().toISOString(),
                    is_anonymous: false // Marquer comme non-anonyme
                })
                .eq('id', currentUser.id)
                .select()
                .single()

            if (profileError) {
                console.error('❌ [Anonymous Auth] Profile update error:', profileError)
                throw profileError
            }

            // console.log('✅ [Anonymous Auth] Account upgraded successfully')

            // 3. Convertir le profil en User
            const user = userProfileToUser(updatedProfile)
            return user

        } catch (error) {
            console.error('❌ [Anonymous Auth] Account upgrade failed:', error)
            throw error
        }
    },

    /**
     * Obtenir les limitations pour les utilisateurs anonymes
     */
    getAnonymousUserLimitations() {
        return {
            canCreateHousehold: false,
            canInviteMembers: false,
            canAccessAdvancedFeatures: false,
            canReceiveNotifications: false,
            maxChildren: 2,
            maxTasks: 10,
            maxRewards: 5
        }
    },

    /**
     * Vérifier si une action est autorisée pour un utilisateur anonyme
     */
    async canAnonymousUserPerformAction(action: string): Promise<boolean> {
        const limitations = this.getAnonymousUserLimitations()

        switch (action) {
            case 'create_household':
                return limitations.canCreateHousehold
            case 'invite_members':
                return limitations.canInviteMembers
            case 'access_advanced_features':
                return limitations.canAccessAdvancedFeatures
            default:
                return true
        }
    }
}
